import { Box, Tooltip, Typography } from '@mui/material';
import { getSlaFromPlatformSlaId } from '@provision/service-level-utils';
import { COPY } from '../../copy';
import type { CtaTarget } from '../../cta';
import { formatDollars, telHref } from '../../format';
import { useLayoutStyles } from '../../layouts/layout-context';
import type { PlanVariant } from '../../layouts/types';
import { CtaButton } from '../../primitives/cta-button';
import { CheckIcon, ClockIcon, PlusIcon } from '../../primitives/icons';
import type { LandingPageProduct } from '../../types';
import type { PlanRows } from './plan-rows';
import type { BillingPeriod } from './plans-block';

interface PlanCardProps {
  product: LandingPageProduct;
  rows: PlanRows;
  variant: PlanVariant;
  isMostPopular: boolean;
  period: BillingPeriod;
  showPricing: boolean;
  savingsPercent: number | null;
  callForPricing: boolean;
  callForPricingPhone?: string;
  cta: CtaTarget;
}

export const PlanCard = ({
  product,
  rows,
  variant,
  isMostPopular,
  period,
  showPricing,
  savingsPercent,
  callForPricing,
  callForPricingPhone,
  cta,
}: PlanCardProps) => {
  const s = useLayoutStyles('plans');
  const price = product.prices[period];
  const responseMinutes = getSlaFromPlatformSlaId(product.platformSlaId);

  return (
    <Box component="article" data-variant={variant} sx={s.card[variant]}>
      {isMostPopular ? <Box sx={s.flag}>{COPY.mostPopular}</Box> : null}
      <Typography component="h3" sx={s.tier[variant]}>
        {product.name}
      </Typography>

      {showPricing && callForPricing ? (
        <>
          <Box sx={s.priceRow}>
            <Box component="span" sx={s.priceCallForPricing}>
              {COPY.callForPricing}
            </Box>
          </Box>
          <Box sx={s.priceNote[variant]}>
            {callForPricingPhone ? (
              <a href={telHref(callForPricingPhone)}>{COPY.callPhone(callForPricingPhone)}</a>
            ) : (
              COPY.tailored(product.name)
            )}
          </Box>
        </>
      ) : null}

      {showPricing && !callForPricing && price ? (
        <>
          <Box sx={s.priceRow}>
            <Box component="span" sx={s.price[variant]}>
              {formatDollars(period === 'annual' ? price.amountCents / 12 : price.amountCents)}
            </Box>
            <Box component="span" sx={s.per[variant]}>
              {COPY.perMonth}
            </Box>
          </Box>
          <Box sx={s.priceNote[variant]}>
            {period === 'annual' ? COPY.billedAnnually(savingsPercent) : COPY.billedMonthly}
          </Box>
        </>
      ) : null}

      <Box component="hr" sx={s.rule[variant]} />

      {responseMinutes !== null ? (
        <Box sx={s.responsePill[variant]}>
          <ClockIcon />
          {COPY.responseWithin(responseMinutes)}
        </Box>
      ) : null}

      <Box component="ul" sx={s.featureList}>
        {rows.everythingIn ? (
          <Box component="li" sx={s.featurePlus[variant]}>
            <PlusIcon sx={s.featurePlusIcon[variant]} />
            <span>{COPY.everythingIn(rows.everythingIn)}</span>
          </Box>
        ) : null}
        {rows.rows.map((row) => (
          <Box component="li" key={row.id} sx={s.feature[variant]}>
            <CheckIcon sx={s.featureIcon[variant]} />
            <span>
              {row.text}
              {row.tooltip ? (
                <Tooltip title={row.tooltip}>
                  <Box
                    component="span"
                    sx={s.tip[variant]}
                    tabIndex={0}
                    aria-label={row.tooltip}
                  >
                    i
                  </Box>
                </Tooltip>
              ) : null}
            </span>
          </Box>
        ))}
      </Box>

      <Box sx={s.cta}>
        <CtaButton target={cta} variant={s.cardCta[variant]} />
      </Box>
    </Box>
  );
};
